import type { User } from '@supabase/supabase-js';

/**
 * Safely narrows unknown JSON-like values to plain object records.
 */
function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

/**
 * Returns the string value when present, otherwise null.
 */
function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Reads the head branch ref name from metadata payloads shaped like:
 * `{ pullRequest: { head: { ref: string } } }`.
 */
export function readHeadBranchName(
  metadata: unknown,
  fallbackHeadRef?: string | null,
): string | null {
  const root = readRecord(metadata);
  const pullRequest = readRecord(root?.pullRequest);
  const head = readRecord(pullRequest?.head);
  return readString(head?.ref) ?? fallbackHeadRef ?? null;
}

/**
 * Normalizes pull request state checks to case-insensitive "open".
 */
export function isPullRequestOpen(state: string | null | undefined): boolean {
  return (state ?? '').toLowerCase() === 'open';
}

/** Whether the signed-in user wrote the pull request, by GitHub login, which ignores case. */
export function isPullRequestAuthor(
  user: Pick<User, 'user_metadata'> | null,
  authorLogin: string | null | undefined,
): boolean {
  const login: unknown = user?.user_metadata.user_name;
  return (
    typeof login === 'string' && !!authorLogin && login.toLowerCase() === authorLogin.toLowerCase()
  );
}
